export type UTMParams = Partial<{
  source: string;
  medium: string;
  campaign: string;
  term: string;
  content: string;
  gclid: string;
}>;

export type LeadInfo = Partial<{
  name: string;
  email: string;
  phone: string;
  company: string;
  utm: UTMParams;
  referrer: string;
  timestamp: number;
}>;

const KEY = "lead_info";

const safeWindow = () => (typeof window !== "undefined" ? window : undefined);

export const getUTMParams = (): UTMParams => {
  const w = safeWindow();
  if (!w) return {};
  const params = new URLSearchParams(w.location.search);
  const map = (k: string) => params.get(k) || undefined;
  return {
    source: map("utm_source") || undefined,
    medium: map("utm_medium") || undefined,
    campaign: map("utm_campaign") || undefined,
    term: map("utm_term") || undefined,
    content: map("utm_content") || undefined,
    gclid: map("gclid") || undefined,
  };
};

export const isAdTraffic = (): boolean => {
  const w = safeWindow();
  if (!w) return false;
  try {
    const params = new URLSearchParams(w.location.search);
    const utmSource = params.get("utm_source");
    const gclid = params.get("gclid");
    if (gclid || (utmSource && utmSource.toLowerCase().includes("google"))) return true;

    const stored = loadLeadInfo();
    if (stored?.utm?.gclid) return true;
    if (stored?.utm?.source && stored.utm.source.toLowerCase().includes("google")) return true;
  } catch {
    // ignore
  }
  return false;
};

export const loadLeadInfo = (): LeadInfo | undefined => {
  const w = safeWindow();
  if (!w) return undefined;
  try {
    const raw = w.localStorage.getItem(KEY);
    if (!raw) return undefined;
    return JSON.parse(raw);
  } catch {
    return undefined;
  }
};

export const saveLeadInfo = (info: LeadInfo) => {
  const w = safeWindow();
  if (!w) return;
  try {
    const merged = { ...(loadLeadInfo() || {}), ...info, timestamp: Date.now() } as LeadInfo;
    w.localStorage.setItem(KEY, JSON.stringify(merged));
  } catch {
    // no-op
  }
};

export const clearLeadInfo = () => {
  const w = safeWindow();
  if (!w) return;
  try {
    w.localStorage.removeItem(KEY);
  } catch {
    // no-op
  }
};

// ---- Ad-level attribution (Meta / Google) ---------------------------------
// Full provenance captured from the landing URL, persisted so it survives the
// user navigating around before they submit a form.

export type AdAttribution = Partial<{
  source: string;      // utm_source -> channel (meta, google, ...)
  medium: string;      // utm_medium
  campaign: string;    // utm_campaign
  campaign_id: string; // {{campaign.id}}
  adset_id: string;    // {{adset.id}}
  ad_id: string;       // {{ad.id}} -- the EXACT ad
  ad_name: string;     // {{ad.name}} (or utm_content)
  fbclid: string;      // Meta click id
  gclid: string;       // Google click id
  landing_url: string; // first page they arrived on
  referrer: string;
}>;

const ATTR_KEY = "lead_attribution";

// Read attribution from the current URL. Meta dynamic params can be passed as
// ad_id/adset_id/campaign_id/ad_name (via URL parameters {{ad.id}} etc.) or fall
// back to the standard utm_* set; utm_content doubles as ad_name when unset.
export const readAttributionFromUrl = (): AdAttribution => {
  const w = safeWindow();
  if (!w) return {};
  const p = new URLSearchParams(w.location.search);
  const g = (k: string) => p.get(k) || undefined;
  const attr: AdAttribution = {
    source: g("utm_source"),
    medium: g("utm_medium"),
    campaign: g("utm_campaign"),
    campaign_id: g("campaign_id") || g("utm_campaign_id"),
    adset_id: g("adset_id") || g("utm_adset_id"),
    ad_id: g("ad_id") || g("utm_ad_id"),
    ad_name: g("ad_name") || g("utm_content"),
    fbclid: g("fbclid"),
    gclid: g("gclid"),
  };
  (Object.keys(attr) as (keyof AdAttribution)[]).forEach((k) => attr[k] == null && delete attr[k]);
  return attr;
};

// Capture on page load and merge into localStorage (first-touch wins per field:
// we only fill blanks so the original ad that brought them isn't overwritten by
// a later internal navigation that drops the params).
export const captureAttributionOnLoad = () => {
  const w = safeWindow();
  if (!w) return;
  try {
    const fresh = readAttributionFromUrl();
    const hasSignal = Object.keys(fresh).length > 0;
    const stored: AdAttribution = JSON.parse(w.localStorage.getItem(ATTR_KEY) || "{}");
    if (!stored.landing_url) fresh.landing_url = w.location.href;
    if (!stored.referrer && w.document.referrer) fresh.referrer = w.document.referrer;
    if (!hasSignal && stored.landing_url) return; // nothing new and already have a touch
    const merged: AdAttribution = { ...fresh, ...stored }; // stored (first-touch) wins
    w.localStorage.setItem(ATTR_KEY, JSON.stringify(merged));
  } catch {
    // no-op
  }
};

export const loadAttribution = (): AdAttribution => {
  const w = safeWindow();
  if (!w) return {};
  try {
    return JSON.parse(w.localStorage.getItem(ATTR_KEY) || "{}");
  } catch {
    return {};
  }
};

// Fire-and-forget: post a captured lead to the D9L dashboard (Neon) with its full
// ad-level attribution. Never throws / never blocks the existing form flow.
export const submitLeadToDashboard = (lead: {
  name?: string;
  email?: string;
  phone?: string;
  company?: string;
  source?: string;     // which form / page
  notes?: string;
}) => {
  const w = safeWindow();
  if (!w) return;
  try {
    const a = loadAttribution();
    const body = {
      name: lead.name,
      email: lead.email,
      phone: lead.phone,
      company: lead.company,
      form: lead.source,
      notes: lead.notes,
      channel: a.source,
      medium: a.medium,
      campaign: a.campaign,
      campaign_id: a.campaign_id,
      adset_id: a.adset_id,
      ad_id: a.ad_id,
      ad_name: a.ad_name,
      fbclid: a.fbclid,
      gclid: a.gclid,
      landing_url: a.landing_url,
      referrer: a.referrer,
    };
    void fetch("/api/d9l/lead-capture", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {});
  } catch {
    // no-op
  }
};
